import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import Swal from "sweetalert2";
import { formatPrice } from "../../helpers/utility";
import { clientActions } from "../../sagas/clientSlice";
import Select from "react-select";
import { productActions } from "../../sagas/productSlice";
import * as Yup from 'yup'; // Import Yup for validation
import { commandeActions } from "../../sagas/commandeSlice";
import CommandeLine from "./CommandeLine";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { transporteurActions } from "../../sagas/transporteurSlice";
import { reglementActions } from "../../sagas/reglementSlice";
import { FaArrowDown, FaArrowUp, FaBalanceScale, FaCalendarAlt, FaMoneyBillWave, FaShieldAlt } from "react-icons/fa";

const initForm = {
    clientId: null,
    chantierId: null,
    lignes: [],
    dateOperation: new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000).toISOString().split('T')[0],
    remarques: '',
    totalAmountHt: 0,
    totalAmountTtc: 0,
    totalAmountTva: 0,
    status: 'EN_ATTENTE',
    entrepotId: null,
    transporteurId: null,
};

const BCForm = ({ onClose, commande }) => {
    const { t } = useTranslation('translation');
    const [formState, setForm] = useState(initForm);
    const dispatch = useDispatch();
    const clients = useSelector(state => state.clients.clients);
    const products = useSelector(state => state.products.products);
    const [currentPage, setCurrentPage] = useState(1);
    const { entrepots } = useSelector(state => state.entrepots);
    const { transporteurs } = useSelector(state => state.transporteurs);
    const [selectedClient, setSelectedClient] = useState(null);
    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const [selectedChantier, setSelectedChantier] = useState(null);
    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });

    useEffect(() => {
        if (commande) {
            setForm({ ...commande });
        } else {
            setForm(initForm);
        }

        dispatch(entrepotActions.list());
        dispatch(transporteurActions.list());
    }, [commande]);

    // Validation schema
    const validationSchema = Yup.object().shape({
        clientId: Yup.number()
            .required(t('commande.clientRequired')), // Ensure a client is selected
        entrepotId: Yup.number()
            .required(t('commande.entrepotRequired')), // Ensure an entrepot is selected
        lignes: Yup.array().min(1, t('commande.validation.atLeastOneItem')), // Ensure at least one item is added
    });

    const formik = useFormik({
        initialValues: {
            ...formState,
            clientId: commande ? commande.clientId : null,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            if (commande) {
                let payload = {
                    id: commande.id,
                    data: values,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.updated'),
                            text: t('commande.updatedSuccess'),
                        });
                        onClose();
                    },
                };
                dispatch(commandeActions.update(payload));
            } else {
                let payload = {
                    data: values,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.saved'),
                            text: t('commande.savedSuccess'),
                        });
                        onClose();
                    },
                };
                dispatch(commandeActions.add(payload));
            }
        },
    });

    useEffect(() => {
        // Charger la liste des clients si elle n'est pas déjà disponible
        if (!clients || clients.length === 0) {
            dispatch(clientActions.search({
                query: ""
            }));
        }

        if (!products || products.length === 0) {
            dispatch(productActions.findAll({
                search: "",
                page: currentPage - 1,
                size: 10,
            }));
        }

        if (clients && commande) {
            setForm({ ...commande });
            if (commande.chantierNom && commande.clientId) {
                const client = clients.find(c => c.id === commande.clientId);
                if (client) {
                    const chantier = client.chantiers.find(ch => ch.nom === commande.chantierNom);
                    setSelectedChantier(chantier ? { value: chantier.id, label: chantier.nom } : null);
                    setSelectedClient({
                        value: client.id,
                        label: client.typeClient === 'PARTICULIER' ? `${client.nom} ${client.prenom || ''}` : client.entreprise,
                        chantiers: client.chantiers,
                        solde: client.solde,
                        totalDebit: client.totalDebit,
                        totalCredit: client.totalCredit
                    });
                    dispatch(reglementActions.echeanceClient({ ...filters, clientId: client.id }));
                    dispatch(reglementActions.cautionClient({ ...filters, clientId: client.id }));
                }
            }
        }

    }, [commande, clients, dispatch]);


    const { values, getFieldProps, handleSubmit, setFieldValue, errors, touched } = formik;
    // Map clients to options for React-Select
    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        chantiers: client.chantiers,
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
    })) ?? [];

    const entrepotOptions = (entrepots.filter(e => e.libelle !== 'EXTERNE' && e.libelle !== 'RETOUR') ?? [])?.map(entrepot => ({
        value: entrepot.id,
        label: entrepot.code + ' - ' + entrepot.libelle,
    })) ?? [];

    const transporteurOptions = transporteurs?.map(transporteur => ({
        value: transporteur.id,
        label: transporteur.code + ' - ' + transporteur.libelle,
    })) ?? [];

    // Calculate montantTotalTaxe and montantTotalTTC
    const montantTotalTaxe = values.montantTotalTaxe || 0;
    const montantTotalTTC = values.montantTotalTTC || 0;
    const montantTotalHT = values.montantTotalHT || 0;

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };


    return (
        <Container fluid>
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <Card>
                    <CardHeader>
                        <Row>
                            <Col md="3">
                                <Label htmlFor="clientId">{t('bonLivraison.client')}:</Label>
                                <Select
                                    id="clientId"
                                    options={clientOptions}
                                    value={clientOptions.find(option => option.value === values.clientId)}
                                    onChange={(selectedOption) => {
                                        setFieldValue('clientId', selectedOption.value);
                                        setSelectedClient(selectedOption);
                                        dispatch(reglementActions.echeanceClient({ ...filters, clientId: selectedOption.value }));
                                        dispatch(reglementActions.cautionClient({ ...filters, clientId: selectedOption.value }));
                                        setFilters(f => ({ ...f, clientId: selectedOption.value }));
                                    }}
                                    placeholder={t('bonLivraison.selectClient')}
                                    isSearchable
                                    isDisabled={commande && commande.status === 'VALIDE' ? true : false}
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                                {errors.clientId && touched.clientId && (
                                    <div className="text-danger small mt-1">{errors.clientId}</div>
                                )}
                            </Col>
                           
                            
                            <Col md="3">
                                <Label htmlFor="dateOperation">{t('commande.dateOperation')}:</Label>
                                <Input
                                    type="date"
                                    id="dateOperation"
                                    {...getFieldProps('dateOperation')}
                                    className="form-control"
                                    disabled={commande && commande.status === 'VALIDE' ? true : false}
                                />
                            </Col>
                             <Col md="3">
                                <Label htmlFor="entrepotId">{t('commande.entrepot')}:</Label>
                                <Select
                                    id="entrepotId"
                                    options={entrepotOptions}
                                    value={entrepotOptions.find(option => option.value === values.entrepotId)}
                                    onChange={(selectedOption) => setFieldValue('entrepotId', selectedOption.value)}
                                    placeholder={t('commande.selectEntrepot')}
                                    isSearchable
                                    isDisabled={commande && commande.status === 'VALIDE' ? true : false}
                                />
                                {errors.entrepotId && touched.entrepotId && (
                                    <div className="text-danger small mt-1">{errors.entrepotId}</div>
                                )}
                            </Col>
                            <Col md="3">
                                <Label for="modeReglement">{t('commande.modeReglement')}</Label>
                                <Input
                                    type="select"
                                    name="modeReglement"
                                    id="modeReglement"
                                    value={values.modeReglement || 30}
                                    onChange={(e) => setFieldValue('modeReglement', e.target.value)}
                                    disabled={commande && commande.status === 'VALIDE' ? true : false}
                                >
                                    <option value={15}>15 {t('devis.jours')}</option>
                                    <option value={30}>30 {t('devis.jours')}</option>
                                    <option value={45}>45 {t('devis.jours')}</option>
                                    <option value={60}>60 {t('devis.jours')}</option>
                                    <option value={90}>90 {t('devis.jours')}</option>
                                    <option value="COMPTANT">{t('commande.comptant')}</option>
                                </Input>
                            </Col>
                        </Row>
                        {selectedClient && (
                            <Row className="mt-2 g-2">
                                <Col>
                                    <Row className="g-2 align-items-stretch">
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaBalanceScale className="mb-1 text-success" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.balance')}</span>
                                                    {selectedClient.totalCredit > selectedClient.totalDebit ? (
                                                        <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>+{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : selectedClient.totalCredit < selectedClient.totalDebit ? (
                                                        <span className="fw-bold text-danger" style={{ fontSize: '0.9rem' }}>-{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : (
                                                        <span className="fw-bold text-muted" style={{ fontSize: '0.9rem' }}>{formatPrice(0)}</span>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaArrowDown className="mb-1 text-danger" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalDebit')}</span>
                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedClient.totalDebit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaArrowUp className="mb-1 text-primary" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalCredit')}</span>
                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedClient.totalCredit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <Card className="shadow-sm h-100 bg-warning bg-opacity-10 border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <FaCalendarAlt className="me-1 text-warning" size={16} />
                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.echeances')}</span>
                                                    </div>
                                                    <span className="fw-bold text-warning" style={{ fontSize: '0.9rem' }}>{echeances?.length || 0}</span>
                                                    <Button color="link" size="sm" className="p-0 fw-bold text-warning" style={{ fontSize: '0.7rem' }} onClick={() => setShowEcheanceModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <Card className="shadow-sm h-100 bg-info bg-opacity-10 border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <FaShieldAlt className="me-1 text-info" size={16} />
                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.cautions')}</span>
                                                    </div>
                                                    <span className="fw-bold text-info" style={{ fontSize: '0.9rem' }}>{cautions?.length || 0}</span>
                                                    <Button color="link" size="sm" className="p-0 fw-bold text-info" style={{ fontSize: '0.7rem' }} onClick={() => setShowCautionModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        )}
                       
                        <Row>
                            <Col md="6">
                                <Label htmlFor="remarques">{t('commande.remarques')}:</Label>
                                <Input
                                    type="textarea"
                                    id="remarques"
                                    {...getFieldProps('remarques')}
                                    className="form-control"
                                    disabled={commande && commande.status === 'VALIDE' ? true : false}
                                />
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="modeReglement">
                                        <FaMoneyBillWave className="me-1" />
                                        {t('facture.modeReglement')} *
                                    </Label>
                                    <Input
                                        type="select"
                                        id="modeReglement"
                                        name="modeReglement"
                                        value={values.modeReglement}
                                        onChange={(e) => setFieldValue('modeReglement', e.target.value)}
                                        disabled={commande && commande.status === 'VALIDE' ? true : false}
                                    >
                                        <option value="">{t('common.select')}</option>
                                        <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                        <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                        <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                        <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <CommandeLine formik={formik} disabled={commande && commande.status === 'VALIDE' ? true : false} />
                        </Row>
                        {errors.lignes && touched.lignes && (
                            <div className="text-danger small mt-1">{errors.lignes}</div>
                        )}
                        <Row className="mt-3 justify-content-end">
                            <Col md="4" className="text-end">
                                <div className="mb-2">
                                    <h5>{t('devis.totalHT')}: {montantTotalHT.toFixed(2)} DH</h5>
                                </div>
                                <div className="mb-2">
                                    <h5>{t('devis.totalTaxe')}: {montantTotalTaxe.toFixed(2)} DH</h5>
                                </div>
                                <div>
                                    <h5>{t('devis.totalTTC')}: {montantTotalTTC.toFixed(2)} DH</h5>
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Button type="submit" color="primary" className="me-2" disabled={commande && commande.status === 'VALIDE' ? true : false}>
                                    {t('common.save')}
                                </Button>
                                <Button type="button" color="secondary" onClick={onClose}>
                                    {t('common.cancel')}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Form>

            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container >
    );
};
export default BCForm;